import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * The availability line: picks its sentence on the CLIENT, and keeps in step with the
 * selected variant.
 *
 * Two problems, one element.
 *
 * 1. CACHING. Liquid runs on the server and a storefront page can be served from cache, so
 *    a server-picked sentence can tell an afternoon visitor that the parcel leaves today
 *    when the cutoff passed hours ago. Both sentences ship in data attributes and the
 *    visitor's own clock decides. Same approach as aniball's pdp-delivery-estimate.
 *    If the page was cached from an earlier DAY, every date in it is wrong - so `data-today`
 *    is compared against the local date and the block falls back to a sentence with no date
 *    in it rather than promising a delivery day that has already passed.
 *
 * 2. VARIANTS. Stock and therefore the whole line differ per variant. Horizon re-renders
 *    only the variant picker and lets other components listen, so this mirrors
 *    `product-inventory.js` rather than inventing a second pattern.
 */
class MbDelivery extends HTMLElement {
  connectedCallback() {
    this.#render();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateAvailability);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateAvailability);
  }

  #localDateISO() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  #render() {
    const target = this.querySelector('[data-mb-delivery-text]');
    const before = this.dataset.before;
    const after = this.dataset.after;

    // A sold-out variant renders no attributes at all; the server sentence is the whole story.
    if (!target || !before || !after) return;

    if (this.dataset.today && this.dataset.today !== this.#localDateISO()) {
      if (this.dataset.fallback) target.textContent = this.dataset.fallback;
      return;
    }

    const cutoff = Number(this.dataset.cutoff);
    if (!Number.isFinite(cutoff)) return;

    const now = new Date();
    const nowHm = now.getHours() * 100 + now.getMinutes();
    target.textContent = nowHm < cutoff ? before : after;
  }

  /**
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateAvailability = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const updated = event.detail.data.html.querySelector('mb-delivery');
    if (!updated) {
      this.replaceChildren();
      return;
    }

    // The sentences belong to the new variant, so take its attributes before re-picking.
    for (const name of ['cutoff', 'today', 'before', 'after', 'fallback']) {
      const value = updated.dataset[name];
      if (value == null) delete this.dataset[name];
      else this.dataset[name] = value;
    }

    morph(this, updated, { childrenOnly: true });
    this.#render();
  };
}

if (!customElements.get('mb-delivery')) {
  customElements.define('mb-delivery', MbDelivery);
}
